import { useMemo, useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { Avatar, Button, Card, EmptyState, Menu } from '@orion/ui';
import type { DealResponse, PipelineResponse } from '@orion/contracts';
import { PageHeader } from '../../app/shell.js';
import { disambiguateNames } from '../../lib/disambiguate-names.js';
import { useAuth } from '../../lib/auth.js';
import { useAssignableMembersQuery } from '../me/api.js';
import { useDealsQuery, useMoveDealStage, usePipelineSummaryQuery, usePipelinesQuery } from './api.js';

type Stage = PipelineResponse['stages'][number];
type View = 'board' | 'list' | 'forecast';

/** Column/header accent, in probability order — matches
 * design/orion-crm-design/rendered-html/DealsPipeline.html's New (blue) →
 * Qualified (violet) → Proposal (teal) → Negotiation (amber) ramp, applied
 * by position rather than hardcoded stage names so any tenant's pipeline
 * configuration still gets a sensible ramp. Won/lost stages use their own
 * fixed green/red treatment instead. */
const OPEN_STAGE_ACCENTS = ['var(--color-primary)', 'var(--color-violet-accent)', 'var(--color-teal)', 'var(--color-amber-deep)'];

function money(value: string | number, currency = 'USD'): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency, maximumFractionDigits: 0 }).format(Number(value));
}

function formatShortDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  const now = new Date();
  if (date.toDateString() === now.toDateString()) return 'Today';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function startOfToday(): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

function currentQuarterRange(): { from: string; to: string } {
  const now = new Date();
  const q = Math.floor(now.getMonth() / 3);
  const from = new Date(now.getFullYear(), q * 3, 1);
  const to = new Date(now.getFullYear(), q * 3 + 3, 0);
  return { from: from.toISOString().slice(0, 10), to: to.toISOString().slice(0, 10) };
}

const VALUE_PRESETS = [
  { label: 'Any value', min: undefined, max: undefined },
  { label: 'Under $25k', min: undefined, max: 25000 },
  { label: '$25k – $100k', min: 25000, max: 100000 },
  { label: 'Over $100k', min: 100000, max: undefined },
] as const;

function ChevronIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#5E6470" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

function ClockIcon({ stroke = '#5E6470' }: { stroke?: string }) {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth="2" strokeLinecap="round" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  );
}

/** docs section 13 "Opportunities / Deals" — Kanban board with drag and
 * drop between stages. Native HTML5 drag events (no new dependency); moving
 * a card calls the real stage-move endpoint, which records stage history
 * and requires a loss reason for a 'lost' stage.
 *
 * design/orion-crm-design/rendered-html/DealsPipeline.html's screenshot
 * captures a card mid-drag (a floating "Fjord Analytics" card, a dashed
 * gap left behind in Proposal, and a highlighted drop-target ring around
 * Negotiation) — a live interaction snapshot, not a resting page state. A
 * static page load can't reproduce all three at once without permanently
 * faking a drag that isn't happening, so that region is a disclosed,
 * unfixable diff source; the real onDragOver/onDrop handlers below do
 * produce the drop-target styling during an actual drag.
 *
 * There's no "hot deal" flame in this data: unlike Lead's `temperature`,
 * Opportunity has no equivalent field, so it's omitted rather than invented. */
export function DealsPipelinePage() {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const { data: pipelines, isLoading: pipelinesLoading } = usePipelinesQuery();
  const { data: members } = useAssignableMembersQuery();
  const memberLabels = useMemo(() => disambiguateNames(members ?? []), [members]);
  const [pipelineId, setPipelineId] = useState<string | undefined>(undefined);
  const [view, setView] = useState<View>('board');
  const [q, setQ] = useState('');
  const [ownerId, setOwnerId] = useState<string | undefined>(undefined);
  const [closeDateQuarter, setCloseDateQuarter] = useState(true);
  const [valuePreset, setValuePreset] = useState<(typeof VALUE_PRESETS)[number]>(VALUE_PRESETS[0]);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [dragDealId, setDragDealId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const pipeline = pipelines?.find((p) => p.id === pipelineId) ?? pipelines?.[0];
  const quarter = useMemo(() => currentQuarterRange(), []);
  const exportUrl = `/api/v1/deals/export${q ? `?q=${encodeURIComponent(q)}` : ''}`;

  // 100 is the maximum the shared list schema allows (packages/contracts,
  // common/envelope.ts) — a pipeline with more than 100 matching deals is
  // still cut off in the cards rendered per column, which wants real
  // pagination rather than a bigger number. The header/column totals below
  // come from the uncapped GET /deals/pipeline-summary instead.
  const { data, isLoading } = useDealsQuery({
    pipelineId: pipeline?.id,
    pageSize: 100,
    q: q || undefined,
    ownerId,
    expectedCloseDateFrom: closeDateQuarter ? quarter.from : undefined,
    expectedCloseDateTo: closeDateQuarter ? quarter.to : undefined,
    amountMin: valuePreset.min,
    amountMax: valuePreset.max,
  });
  const { data: summary } = usePipelineSummaryQuery(pipeline?.id);
  const moveStage = useMoveDealStage();

  const deals = data?.data ?? [];

  const handleDrop = async (stageId: string, stageType: string) => {
    if (!dragDealId) return;
    const dealId = dragDealId;
    setDragDealId(null);
    if (stageType === 'lost') {
      // A loss reason is required server-side — for now, block the drop and
      // point the rep at the deal's own page where a reason picker belongs.
      setError('Open the deal to mark it lost — a loss reason is required.');
      return;
    }
    setError(null);
    try {
      await moveStage.mutateAsync({ id: dealId, input: { stageId } });
    } catch {
      setError('Could not move that deal. Please try again.');
    }
  };

  const toggleExpand = (stageId: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(stageId)) next.delete(stageId);
      else next.add(stageId);
      return next;
    });
  };

  if (pipelinesLoading || isLoading) return <PageHeader breadcrumb={[{ label: 'Opportunities' }]} />;
  if (!pipeline) {
    return (
      <>
        <PageHeader breadcrumb={[{ label: 'Opportunities' }]} />
        <main className="p-7">
          <EmptyState title="No pipeline configured" description="An admin needs to set up a pipeline before opportunities can be tracked." />
        </main>
      </>
    );
  }

  const stageAccent = (() => {
    const openStages = pipeline.stages.filter((s) => s.type === 'open');
    const map = new Map<string, string>();
    openStages.forEach((s, i) => map.set(s.id, OPEN_STAGE_ACCENTS[i % OPEN_STAGE_ACCENTS.length]!));
    return map;
  })();

  return (
    <>
      <PageHeader breadcrumb={[{ label: 'Opportunities', to: '/deals' }, { label: pipeline.name }]} />
      <main className="flex flex-1 flex-col gap-4 overflow-hidden px-7 py-6">
        {error && (
          <div role="alert" className="shrink-0 rounded-control bg-danger-soft px-3 py-2 text-sm text-danger">
            {error}
          </div>
        )}

        <div className="flex h-10 shrink-0 items-center gap-4">
          <h1 className="text-[22px] font-[650] leading-none tracking-[-0.01em] text-ink">Opportunities</h1>
          {(pipelines?.length ?? 0) > 1 ? (
            <Menu
              trigger={
                <button type="button" className="flex h-8 items-center gap-1 rounded-control border border-border-strong bg-surface px-2.5 text-[13px] font-medium text-text-secondary hover:bg-surface-subtle">
                  {pipeline.name}
                  <ChevronIcon />
                </button>
              }
              items={pipelines!.map((p) => ({ label: p.name, onSelect: () => setPipelineId(p.id) }))}
              align="left"
            />
          ) : (
            <span className="flex h-8 items-center gap-1 rounded-control border border-border-strong bg-surface px-2.5 text-[13px] font-medium text-text-secondary">
              {pipeline.name}
            </span>
          )}
          {summary && (
            <span className="text-sm text-text-secondary tabular-nums">
              <b className="font-semibold text-ink">{summary.openCount} open</b> · {money(summary.openValue)} ·{' '}
              <span className="text-text-muted">weighted</span> <b className="font-semibold text-ink">{money(summary.weightedValue)}</b>
            </span>
          )}
          <div className="flex-1" />
          <div role="group" aria-label="View" className="flex items-center gap-0.5 rounded-[9px] p-0.5" style={{ background: '#EAE8E2' }}>
            {(
              [
                ['board', 'Board'],
                ['list', 'List'],
                ['forecast', 'Forecast'],
              ] as const
            ).map(([key, label]) => (
              <button
                key={key}
                type="button"
                aria-pressed={view === key}
                onClick={() => setView(key)}
                className={`flex h-7 items-center gap-1.5 rounded-chip px-2.5 text-[13px] font-medium ${view === key ? 'bg-surface text-ink shadow-card' : 'text-text-muted hover:text-ink'}`}
              >
                {label}
              </button>
            ))}
          </div>
          <Link to="/deals/import" className="flex h-8 items-center gap-1.5 rounded-control border border-border-strong bg-surface px-2.5 text-[13px] font-semibold text-text-secondary hover:bg-surface-subtle">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#454A54" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M12 16V4M7 9l5-5 5 5" />
              <path d="M4 16v3a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-3" />
            </svg>
            Import
          </Link>
          {hasPermission('crm.deals.export') && (
            <a href={exportUrl} className="flex h-8 items-center gap-1.5 rounded-control border border-border-strong bg-surface px-2.5 text-[13px] font-semibold text-text-secondary hover:bg-surface-subtle">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#454A54" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M12 4v12M7 11l5 5 5-5" />
                <path d="M4 16v3a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-3" />
              </svg>
              Export
            </a>
          )}
          <Button size="sm" onClick={() => navigate({ to: '/deals/new' })}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
              <path d="M12 5v14M5 12h14" />
            </svg>
            New opportunity
          </Button>
        </div>

        <div className="flex h-8 shrink-0 items-center gap-2">
          <label className="flex h-8 w-60 items-center gap-2 rounded-control border border-border-strong bg-surface px-2.5">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#5E6470" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="11" cy="11" r="7" />
              <path d="m20 20-3.5-3.5" />
            </svg>
            <span className="sr-only">Search opportunities</span>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              type="search"
              placeholder="Search opportunities…"
              className="w-full bg-transparent text-sm text-ink placeholder:text-text-muted focus:outline-none"
            />
          </label>

          <Menu
            trigger={
              <button type="button" className="flex h-8 items-center gap-1 rounded-control border border-border-strong bg-surface px-2.5 text-[13px] text-text-secondary hover:bg-surface-subtle">
                Owner: <span className="font-semibold text-ink">{ownerId ? (memberLabels.get(ownerId) ?? 'Anyone') : 'Anyone'}</span>
                <ChevronIcon />
              </button>
            }
            items={[{ label: 'Anyone', onSelect: () => setOwnerId(undefined) }, ...(members ?? []).map((m) => ({ label: memberLabels.get(m.id) ?? m.displayName, onSelect: () => setOwnerId(m.id) }))]}
            align="left"
          />

          {closeDateQuarter && (
            <span
              className="flex h-8 items-center gap-1.5 rounded-chip border pl-2.5 pr-1.5 text-[13px] font-semibold"
              style={{ background: 'var(--color-primary-soft)', borderColor: 'var(--color-primary-border)', color: 'var(--color-primary-soft-text)' }}
            >
              Close date: This quarter
              <button type="button" aria-label="Clear close date filter" onClick={() => setCloseDateQuarter(false)} className="flex size-4 items-center justify-center">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary-soft-text)" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true">
                  <path d="M6 6l12 12M18 6 6 18" />
                </svg>
              </button>
            </span>
          )}

          <Menu
            trigger={
              <button type="button" className="flex h-8 items-center gap-1 rounded-control border border-border-strong bg-surface px-2.5 text-[13px] text-text-secondary hover:bg-surface-subtle">
                Value: <span className="font-semibold text-ink">{valuePreset.label}</span>
                <ChevronIcon />
              </button>
            }
            items={VALUE_PRESETS.map((preset) => ({ label: preset.label, onSelect: () => setValuePreset(preset) }))}
            align="left"
          />

          <div className="flex-1" />

          {summary && summary.pastDueCount > 0 && (
            <span className="flex items-center gap-1.5 text-[13px] font-semibold text-danger">
              <ClockIcon stroke="#B42318" />
              {summary.pastDueCount} opportunit{summary.pastDueCount === 1 ? 'y' : 'ies'} past close date
            </span>
          )}
        </div>

        {view === 'board' && (
          <div className="grid min-h-0 flex-1 gap-3" style={{ gridTemplateColumns: `repeat(${pipeline.stages.length}, minmax(0, 1fr))` }}>
            {pipeline.stages.map((stage) => {
              const stageDeals = deals.filter((d) => d.stage.id === stage.id);
              const stageSummary = summary?.stages.find((s) => s.stageId === stage.id);
              const isExpanded = expanded.has(stage.id);
              const visible = isExpanded ? stageDeals : stageDeals.slice(0, 3);
              const hiddenCount = stageDeals.length - visible.length;
              return (
                <div
                  key={stage.id}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => handleDrop(stage.id, stage.type)}
                  className="flex min-h-0 flex-col gap-2 overflow-hidden rounded-xl p-1.5"
                  style={{ background: '#EDECE7' }}
                >
                  <StageColumnHeader
                    stage={stage}
                    count={stageSummary?.count ?? stageDeals.length}
                    totalValue={stageSummary?.totalValue ?? String(stageDeals.reduce((sum, d) => sum + Number(d.amount), 0))}
                    accent={stageAccent.get(stage.id) ?? 'var(--color-primary)'}
                  />
                  <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto">
                    {visible.map((deal) => (
                      <DealCard
                        key={deal.id}
                        deal={deal}
                        stage={stage}
                        onDragStart={() => setDragDealId(deal.id)}
                        onNavigate={() => navigate({ to: '/deals/$dealId', params: { dealId: deal.id } })}
                      />
                    ))}
                    {hiddenCount > 0 && (
                      <button type="button" onClick={() => toggleExpand(stage.id)} className="shrink-0 text-center text-[13px] font-medium text-text-muted hover:text-ink">
                        Show {hiddenCount} more
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {view === 'list' && <DealsListView deals={deals} />}
        {view === 'forecast' && <DealsForecastView stages={pipeline.stages} summary={summary} />}
      </main>
    </>
  );
}

function StageColumnHeader({ stage, count, totalValue, accent }: { stage: Stage; count: number; totalValue: string; accent: string }) {
  const isWon = stage.type === 'won';
  const isLost = stage.type === 'lost';
  const bg = isWon ? 'var(--color-success-soft)' : isLost ? 'var(--color-danger-soft)' : 'var(--color-surface)';
  const textColor = isWon ? 'var(--color-success)' : isLost ? 'var(--color-danger)' : 'var(--color-ink)';
  return (
    <div className="flex shrink-0 flex-col gap-1 rounded-[9px]" style={{ background: bg, padding: '10px 10px 9px', boxShadow: isWon || isLost ? undefined : `inset 0 3px 0 ${accent}` }}>
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-1.5 text-[13px] font-[650]" style={{ color: textColor }}>
          {isWon && (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="m5 12 5 5 9-10" />
            </svg>
          )}
          {isLost && (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true">
              <path d="M6 6l12 12M18 6 6 18" />
            </svg>
          )}
          {stage.name}
        </span>
        <span className="rounded-full px-1.75 py-px text-xs font-semibold" style={{ background: isWon || isLost ? 'var(--color-surface)' : 'var(--color-neutral-pill-soft)', color: isWon || isLost ? textColor : 'var(--color-text-secondary)' }}>
          {count}
        </span>
      </div>
      <div className="flex items-center justify-between text-xs tabular-nums" style={{ color: isWon || isLost ? textColor : 'var(--color-text-muted)' }}>
        <span className="font-semibold" style={{ color: isWon || isLost ? textColor : 'var(--color-ink)' }}>
          {money(totalValue)}
        </span>
        <span>{stage.probability}%</span>
      </div>
    </div>
  );
}

function DealCard({ deal, stage, onDragStart, onNavigate }: { deal: DealResponse; stage: Stage; onDragStart: () => void; onNavigate: () => void }) {
  const isWon = stage.type === 'won';
  const isLost = stage.type === 'lost';
  const daysInStage = Math.floor((Date.now() - new Date(deal.stageEnteredAt).getTime()) / 86_400_000);
  const isStale = stage.type === 'open' && stage.rotAfterDays != null && daysInStage > stage.rotAfterDays;
  const isPastDue = stage.type === 'open' && deal.expectedCloseDate != null && new Date(deal.expectedCloseDate) < startOfToday();

  return (
    <Card draggable onDragStart={onDragStart} onClick={onNavigate} className="flex shrink-0 cursor-pointer flex-col gap-1.5 rounded-input p-2.5 hover:shadow-popover">
      <span className="text-[13px] font-semibold leading-4.25 text-ink">{deal.name}</span>
      <span className="text-xs text-text-muted">{deal.company?.name ?? 'No company'}</span>
      <span className={`text-[15px] font-[650] tabular-nums ${isWon ? 'text-success' : isLost ? 'text-text-muted' : 'text-ink'}`}>{money(deal.amount, deal.currency)}</span>
      <div className="flex items-center gap-1.5 border-t border-divider pt-1.75 text-xs text-text-muted">
        <Avatar name={deal.owner?.displayName ?? '?'} size="xs" />
        {isLost ? (
          <span className="flex-1 truncate">
            {deal.lossReason?.name ?? 'Lost'} · {formatShortDate(deal.lostAt)}
          </span>
        ) : isWon ? (
          <span className="flex-1 truncate">Won {formatShortDate(deal.wonAt)}</span>
        ) : (
          <>
            <span className={`flex-1 truncate ${isPastDue ? 'font-semibold text-danger' : ''}`}>
              {deal.expectedCloseDate ? formatShortDate(deal.expectedCloseDate) : 'No close date'}
              {isPastDue ? ' · overdue' : ''}
            </span>
            <span className="flex shrink-0 items-center gap-0.75" style={isStale ? { color: 'var(--color-amber-deep)', fontWeight: 600 } : undefined}>
              <ClockIcon stroke={isStale ? 'var(--color-amber-deep)' : '#5E6470'} />
              {daysInStage}d
            </span>
          </>
        )}
      </div>
    </Card>
  );
}

/** Not a SCREENS.md entry of its own — the design's Board/List/Forecast
 * toggle is chrome on this one screen (only "Board" is ever captured as
 * the active state), so List and Forecast are kept real but minimal: the
 * same already-fetched deals, shown as a flat table or a weighted-by-stage
 * summary, rather than dead buttons that look interactive but do nothing. */
function DealsListView({ deals }: { deals: DealResponse[] }) {
  if (deals.length === 0) return <EmptyState title="No opportunities match these filters" description="Try widening the search, owner, or value filters." />;
  return (
    <div className="min-h-0 flex-1 overflow-y-auto rounded-card border border-border bg-surface">
      <table className="w-full text-sm">
        <thead className="sticky top-0 border-b border-border bg-surface text-xs font-semibold uppercase tracking-[0.06em] text-text-muted">
          <tr>
            <th className="px-4 py-2 text-left">Name</th>
            <th className="px-4 py-2 text-left">Company</th>
            <th className="px-4 py-2 text-left">Stage</th>
            <th className="px-4 py-2 text-right">Amount</th>
            <th className="px-4 py-2 text-left">Owner</th>
            <th className="px-4 py-2 text-left">Close date</th>
          </tr>
        </thead>
        <tbody>
          {deals.map((deal) => (
            <tr key={deal.id} className="border-b border-divider last:border-0">
              <td className="px-4 py-2.5 font-medium text-ink">{deal.name}</td>
              <td className="px-4 py-2.5 text-text-secondary">{deal.company?.name ?? '—'}</td>
              <td className="px-4 py-2.5 text-text-secondary">{deal.stage.name}</td>
              <td className="px-4 py-2.5 text-right tabular-nums text-ink">{money(deal.amount, deal.currency)}</td>
              <td className="px-4 py-2.5 text-text-secondary">{deal.owner?.displayName ?? 'Unassigned'}</td>
              <td className="px-4 py-2.5 text-text-secondary">{deal.expectedCloseDate ? formatShortDate(deal.expectedCloseDate) : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DealsForecastView({ stages, summary }: { stages: Stage[]; summary: { stages: { stageId: string; count: number; totalValue: string }[] } | undefined }) {
  const openStages = stages.filter((s) => s.type === 'open');
  const rows = openStages.map((stage) => {
    const s = summary?.stages.find((x) => x.stageId === stage.id);
    const total = Number(s?.totalValue ?? 0);
    return { stage, total, weighted: total * (stage.probability / 100) };
  });
  const maxWeighted = Math.max(1, ...rows.map((r) => r.weighted));
  return (
    <div className="min-h-0 flex-1 overflow-y-auto rounded-card border border-border bg-surface p-5">
      <p className="mb-4 text-xs font-semibold uppercase tracking-[0.06em] text-text-muted">Weighted forecast by stage</p>
      <div className="flex flex-col gap-3">
        {rows.map(({ stage, total, weighted }) => (
          <div key={stage.id} className="flex items-center gap-3">
            <span className="w-28 shrink-0 truncate text-sm font-medium text-ink">{stage.name}</span>
            <div className="h-6 flex-1 overflow-hidden rounded-sm bg-neutral-pill-soft">
              <div className="h-full rounded-sm bg-primary" style={{ width: `${(weighted / maxWeighted) * 100}%` }} />
            </div>
            <span className="w-24 shrink-0 text-right text-sm tabular-nums text-text-secondary">{money(total)}</span>
            <span className="w-24 shrink-0 text-right text-sm font-semibold tabular-nums text-ink">{money(weighted)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
